import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';

import ExpandableRow from '@/components/ui/ExpandableRow';
import usePlaylistData from '@/hooks/usePlaylistData';
import { Playlist } from '@/lib/api/playlist';
import { Program } from '@/lib/api/program';
import { formatTimeSimple, formatUuidLastPartWithMimeType, getStatusText } from '@/lib/utils/formatters';

interface ExpandableTableRowProps {
  program: Program;
  formatDate: (dateString: string) => string;
  currentTime: Dayjs; // Current time passed from parent component
}

/**
 * Expandable monitoring table row component
 * Displays a program schedule row and shows related playlists when clicked
 */
export const ExpandableTableRow: React.FC<ExpandableTableRowProps> = ({
  program,
  formatDate,
  currentTime,
}) => {
  // Function to determine if a program should be monitored
  // Extends monitoring by 1 minute after the end time
  const isMonitoringProgram = (): boolean => {
    const since = dayjs(program.since);
    const until = dayjs(program.until);
    return currentTime.isAfter(since, 'second') &&
           currentTime.isBefore(until.add(1, 'minute'), 'second');
  };

  // Function to determine if a program is in the past
  const isPastProgram = (): boolean => {
    const until = dayjs(program.until);
    return currentTime.isAfter(until, 'second');
  };

   // Calculate program state
   const isPast = isPastProgram();
   const isMonitoring = isMonitoringProgram();

  // State for row expansion
  const [open, setOpen] = useState(isMonitoring);
  // State to track if the fetch confirmed no playlists exist (for display)
  const [programHasNoPlaylists, setProgramHasNoPlaylists] = useState(false);

  const {
    playlists,
    loading,
    error,
    // refreshData is still returned but might not be needed explicitly anymore
  } = usePlaylistData(
    program.id,
    100, // limit
    0, // offset
    true, // silentRefresh
    // Fetch playlists if the row is expanded or being monitored.
    isMonitoring || open,
    // Auto-refresh every 5 seconds only if monitoring.
    isMonitoring ? 5000 : undefined,
  );

  // Effect to update the display flag when fetch completes and finds no playlists
  useEffect(() => {
    if (!loading && (isMonitoring || open) && playlists.length === 0) {
      setProgramHasNoPlaylists(true);
    } else if (!loading && playlists.length > 0) {
      // Reset if playlists are found later (e.g., after refresh)
      setProgramHasNoPlaylists(false);
    }
    // Reset when closed or monitoring stops, so it re-evaluates on next open/monitor start
    if (!open && !isMonitoring) {
       setProgramHasNoPlaylists(false);
    }
  }, [loading, playlists, open, isMonitoring]);

  // Main cells for the program row
    const programCells = [
    program.id,
    formatDate(program.since),
    formatDate(program.until),
    program.duration,
    program.supplier || '-',
  ];

  // Past programs are grayed out
  const rowSx = isPast ? { backgroundColor: 'rgba(0, 0, 0, 0.1)' } : undefined;

  // Content to display when expanded
  const expandedContent = (
    <>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Table size="small" aria-label="プレイリスト">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>ファイル名</TableCell>
            <TableCell>ステータス</TableCell>
            <TableCell>開始時刻</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>Quarter</TableCell>
            <TableCell>Half</TableCell>
            <TableCell>3/4</TableCell>
            <TableCell>Complete</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                  読み込み中...
                </Typography>
              </TableCell>
            </TableRow>
          ) : programHasNoPlaylists ? ( // Use the new state here
            <TableRow>
              <TableCell colSpan={10} align="center">
                プレイリストが見つかりません
              </TableCell>
            </TableRow>
          ) : (
            playlists.map((playlist: Playlist) => {
              // Use UTC for comparison to ensure consistent timezone handling
              // Determine if a playlist is in the past (playback completed - compared in UTC)
              const isGrayedOut = (): boolean => {
                const until = dayjs.utc(playlist.until);
                return currentTime.utc().isAfter(until);
              };

              return (
                <TableRow
                  key={playlist.id}
                  sx={{
                    ...(isGrayedOut() && { backgroundColor: 'rgba(0, 0, 0, 0.1)' }), // Past playlists are grayed out
                  }}
                >
                  <TableCell>{playlist.id}</TableCell>
                  <TableCell>
                    <Tooltip title={playlist.src} arrow>
                      <span>{formatUuidLastPartWithMimeType(playlist.src, playlist.mimeType)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{getStatusText(playlist.status)}</TableCell>
                  <TableCell>{formatTimeSimple(playlist.since)}</TableCell>
                  <TableCell>{formatTimeSimple(playlist.statsStartAt)}</TableCell>
                  <TableCell>{formatTimeSimple(playlist.statsQuarterAt)}</TableCell>
                  <TableCell>{formatTimeSimple(playlist.statsHalfAt)}</TableCell>
                  <TableCell>{formatTimeSimple(playlist.statsThreeQuartersAt)}</TableCell>
                  <TableCell>{formatTimeSimple(playlist.statsCompleteAt)}</TableCell>
                  <TableCell>{formatTimeSimple(playlist.statsErrorAt)}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </>
  );

  return (
    <ExpandableRow
      id={program.id}
      cells={programCells}
       expandedContent={expandedContent}
       colSpan={6}
       open={open} // Pass the open state
       onToggle={() => setOpen(!open)} // Pass the toggle handler
       rowSx={rowSx}
     />
  );
};

export default ExpandableTableRow;
